import { useEffect, useState } from 'react';
import { Icon } from '../icons/Icon';
import { api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export const ThemeModeration = () => {
    const [queue, setQueue] = useState<any[]>([]);
    const [activeThemes, setActiveThemes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        const [resQueue, resActive] = await Promise.all([
            api.getAdminThemeQueue(),
            api.getAdminThemes() // Need to add this to api.ts or use the one I added
        ]);
        if (resQueue.success) setQueue(resQueue.data || []);
        if (resActive.success) setActiveThemes(resActive.data || []);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleReview = async (themeId: string, action: 'approve' | 'reject') => {
        const res = await api.reviewTheme(themeId, action);
        if (res.success) {
            fetchData();
        } else {
            alert('審査の送信に失敗しました');
        }
    };

    const handleDelete = async (themeId: string) => {
        if (!confirm('本当にテーマを削除しますか？（作品がある場合は削除できません）')) return;
        const res = await api.deleteTheme(themeId);
        if (res.success) {
            fetchData();
        } else {
            alert(res.error || '削除に失敗しました');
        }
    };

    if (loading) return <div className="p-8 text-center text-pop animate-pulse">読み込み中...</div>;

    return (
        <div className="space-y-12">
            {/* Queue Section */}
            <div>
                <h3 className="text-xl font-bold text-ink mb-6 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                    審査待ちテーマ ({queue.length})
                </h3>
                {queue.length === 0 ? (
                    <div className="text-center py-10 border border-dashed border-ink/15 rounded-xl">
                        <p className="text-ink-3 text-sm">現在審査待ちのテーマはありません。</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {queue.map((theme) => (
                            <ThemeCard key={theme.id} theme={theme} onReview={handleReview} isQueue />
                        ))}
                    </div>
                )}
            </div>

            {/* Active Section */}
            <div className="pt-8 border-t border-ink/10">
                <h3 className="text-xl font-bold text-ink mb-6 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    公開中のテーマ ({activeThemes.filter(t => t.status === 'RECRUITING' || t.status === 'VOTING').length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeThemes.filter(t => t.status === 'RECRUITING' || t.status === 'VOTING').map((theme) => (
                        <ThemeCard key={theme.id} theme={theme} onDelete={handleDelete} />
                    ))}
                </div>
            </div>
        </div>
    );
};

const ThemeCard = ({ theme, onReview, onDelete, isQueue }: any) => (
    <Card className={clsx("bg-surface overflow-hidden group transition-all", isQueue ? "border-yellow-500/20" : "border-ink/15")}>
        <div className="h-32 w-full bg-surface overflow-hidden relative">
            {theme.image_url ? (
                <img src={theme.image_url} alt={theme.title} className="w-full h-full object-cover opacity-50 group-hover:opacity-80 transition-opacity" />
            ) : (
                <div className="w-full h-full flex items-center justify-center opacity-20"><Icon name="art" size={40} /></div>
            )}
            <Badge className={clsx("absolute top-2 right-2", isQueue ? "bg-yellow-500 text-black" : "bg-green-500 text-black")}>
                {theme.status}
            </Badge>
        </div>
        <CardHeader className="pb-2">
            <CardTitle className="text-ink text-lg">{theme.title}</CardTitle>
            <CardDescription className="text-xs">提案者: {theme.creator_name}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
            <p className="text-sm text-ink-2 h-16 overflow-y-auto">
                {theme.description}
            </p>
            <div className="flex gap-2">
                {isQueue ? (
                    <>
                        <Button className="flex-1 bg-green-500 hover:bg-green-600 text-black font-bold" onClick={() => onReview(theme.id, 'approve')}>承認</Button>
                        <Button variant="destructive" className="flex-1 font-bold" onClick={() => onReview(theme.id, 'reject')}>拒否</Button>
                    </>
                ) : (
                    <Button variant="destructive" className="w-full font-bold opacity-50 group-hover:opacity-100 transition-opacity" onClick={() => onDelete(theme.id)}>
                        削除する
                    </Button>
                )}
            </div>
        </CardContent>
    </Card>
);

import clsx from 'clsx';
